import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../styles/shopSidebar.css";
import { useShopAuth } from "../context/ShopAuthContext";
import { toAbsUrl } from "../utils/api";
import { getUnreadMessageCount } from "../utils/messagesApi";

export const SHOP_SIDEBAR_WIDTH = 260;
export const SHOP_COLLAPSED_WIDTH = 68;

function formatMscFromCents(cents) {
  const value = Number(cents || 0) / 100;
  return `${value.toFixed(2)} MSC`;
}

// Unread message count for the Messages badge: every 60s while the tab is visible.
function useUnreadMessages() {
  const [count, setCount] = useState(0);
  const location = useLocation();
  useEffect(() => {
    let alive = true;
    const visible = () => { try { return document.visibilityState === "visible"; } catch { return true; } };
    const load = () => {
      if (!visible()) return;
      getUnreadMessageCount().then((n) => { if (alive) setCount(n); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    const onVis = () => { if (visible()) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [location.pathname]);
  return count;
}

export default function ShopSidebar({ collapsed = false, onToggle, isMobile = false, drawerOpen = false }) {
  const { shop, theme, setTheme } = useShopAuth();
  const shopWalletMsc = formatMscFromCents(shop?.wallet?.mscCents);

  const handleLinkClick = () => {
    onToggle?.(true);
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const linkClass = ({ isActive }) => "ss-item" + (isActive ? " active" : "");

  // Mobile drawer mode
  if (isMobile) {
    return (
      <aside className={`ss ss-drawer ${drawerOpen ? "ss-drawer--open" : ""}`}>
        <div className="ss-top">
          {/* Header row: logo + theme toggle */}
          <div className="ss-drawer-header">
            <div className="ss-drawer-brand">
              <img src="/logo192.png" alt="Moondala" style={{ width: 28, height: 28 }} />
              <span className="ss-drawer-title">Moondala Shop</span>
            </div>
            <button
              type="button"
              className="ss-theme-toggle"
              onClick={toggleTheme}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Shop Profile Card */}
          <div className="ss-profile">
            <div className="ss-avatar">
              {(shop?.logoUrl || shop?.logo) ? (
                <img
                  src={toAbsUrl(shop.logoUrl || shop.logo)}
                  alt={shop?.shopName || "Shop"}
                  className="ss-avatar-img"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div className="ss-avatar-fallback" style={{ display: (shop?.logoUrl || shop?.logo) ? 'none' : 'flex' }}>
                {(shop?.shopName || "S").charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="ss-profile-info">
              <div className="ss-shop-name">{shop?.shopName || "Your Shop"}</div>
              <div className="ss-shop-email">{shop?.shopEmail || ""}</div>
              <div className="ss-wallet">Wallet: {shopWalletMsc}</div>
            </div>
          </div>

          <SidebarNav linkClass={linkClass} handleLinkClick={handleLinkClick} />
        </div>

        <div className="ss-bottom">
          <NavLink to="/shop/settings" className={linkClass} onClick={handleLinkClick}>
            <span className="ss-ico">⚙️</span>
            <span className="ss-label">Settings</span>
          </NavLink>
        </div>
      </aside>
    );
  }

  // Desktop mode: if collapsed, don't render
  if (collapsed) return null;

  return (
    <aside className="ss">
      <div className="ss-top">
        {/* Header row with theme toggle */}
        <div className="ss-drawer-header">
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="ss-theme-toggle"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
        </div>

        {/* Shop Profile Card */}
        <div className="ss-profile">
          <div className="ss-avatar">
            {(shop?.logoUrl || shop?.logo) ? (
              <img
                src={toAbsUrl(shop.logoUrl || shop.logo)}
                alt={shop?.shopName || "Shop"}
                className="ss-avatar-img"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className="ss-avatar-fallback" style={{ display: (shop?.logoUrl || shop?.logo) ? 'none' : 'flex' }}>
              {(shop?.shopName || "S").charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="ss-profile-info">
            <div className="ss-shop-name">{shop?.shopName || "Your Shop"}</div>
            <div className="ss-shop-email">{shop?.shopEmail || ""}</div>
            <div className="ss-wallet">Wallet: {shopWalletMsc}</div>
          </div>
        </div>

        <SidebarNav linkClass={linkClass} handleLinkClick={handleLinkClick} />
      </div>

      <div className="ss-bottom">
        <NavLink to="/shop/settings" className={linkClass} onClick={handleLinkClick}>
          <span className="ss-ico">⚙️</span>
          <span className="ss-label">Settings</span>
        </NavLink>
      </div>
    </aside>
  );
}

/** Shared nav links */
function SidebarNav({ linkClass, handleLinkClick }) {
  const unread = useUnreadMessages();
  return (
    <nav className="ss-nav">
      <NavLink to="/shop/dashboard" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🏠</span>
        <span className="ss-label">Home</span>
      </NavLink>
      <NavLink to="/shop/feed" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">📝</span>
        <span className="ss-label">Feed</span>
      </NavLink>
      <NavLink to="/shop/orders" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">📥</span>
        <span className="ss-label">Orders</span>
      </NavLink>
      <NavLink to="/shop/products" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🏷️</span>
        <span className="ss-label">Products</span>
      </NavLink>
      <NavLink to="/shop/customers" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">👥</span>
        <span className="ss-label">Customers</span>
      </NavLink>
      <NavLink to="/shop/earnings" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">💰</span>
        <span className="ss-label">Earnings</span>
      </NavLink>
      <NavLink to="/shop/withdrawal" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">💎</span>
        <span className="ss-label">MSC Withdrawal</span>
      </NavLink>
      <NavLink to="/shop/shipping" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🚚</span>
        <span className="ss-label">Shipping</span>
      </NavLink>
      <NavLink to="/shop/returns" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">↩️</span>
        <span className="ss-label">Returns</span>
      </NavLink>
      <NavLink to="/shop/cart-watchers" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🛒</span>
        <span className="ss-label">Cart Leads</span>
      </NavLink>
      <NavLink to="/shop/reviews" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">⭐</span>
        <span className="ss-label">Reviews</span>
      </NavLink>
      <NavLink to="/shop/views" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">📊</span>
        <span className="ss-label">Analytics</span>
      </NavLink>

      <div className="ss-section-title">Sales channels</div>

      <NavLink to="/shop/mall/preview" target="_blank" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🏪</span>
        <span className="ss-label">Online Store</span>
        <span className="ss-external">↗</span>
      </NavLink>
      <NavLink to="/shop/mall/edit" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">🎨</span>
        <span className="ss-label">Editor</span>
      </NavLink>

      <div className="ss-section-title">Apps</div>

      <NavLink to="/shop/messages" className={linkClass} onClick={handleLinkClick}>
        <span className="ss-ico">💬</span>
        <span className="ss-label">Messages</span>
        {unread > 0 && (
          <span
            className="ss-badge"
            style={{ marginLeft: "auto", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 10, background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </NavLink>
    </nav>
  );
}
